import { Box, Paper, Typography, Stack, alpha, useTheme, Chip, Button, Avatar, Divider, IconButton, Tooltip, Skeleton } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuShare2, LuLayoutTemplate, LuShare, LuFileText, LuBox, LuFolders, LuBuilding } from "react-icons/lu";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../components/button/RefreshButton";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogGeneral from "../../components/dialog/DialogGeneral";
import SharePortal from "../../components/dialog/SharePortal";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { PublishedTemplate } from "../../db_access/published_template";
import { TemplateSharing } from "../../db_access/template_sharing";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import dayjs from "dayjs";

const PublishedTemplateGrid = ({ selectedCompanyID, type }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const { refresh, setRefresh, columnVisibilities, enqueueAlert } = useContextProvider();

    const gridRef = useRef(null);
    const dialogRef = useRef(null);
    const gridHeaderContainerRef = useRef(null);

    const [templates, setTemplates] = useState([]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [searchText, setSearchText] = useState("");
    const colVisibility_key = "published_template";
    const [columnMenuVisible, setColumnMenuVisible] = useState(false);

    // Share Portal State
    const [sharePortalOpen, setSharePortalOpen] = useState(false);
    const [shareTarget, setShareTarget] = useState(null);

    const accentColorByCat = {
        product: '#3b82f6',
        project: '#10b981',
        building: '#f59e0b',
        structure: '#8b5cf6'
    };
    const activeColor = accentColorByCat[type] || '#16a34a';

    const colDefs = useMemo(() => {
        const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
        const types = {
            product: t("Product"),
            project: t("Project"),
            building: t("Building"),
            supply_chain: t("Supply Chain"),
            structure: t("Organization"),
        };

        return [
            {
                headerName: t("Share"),
                field: "actions",
                width: 80,
                pinned: "left",
                cellRenderer: (p) => (
                    <Tooltip title={t("Share to Organizations")}>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setShareTarget([p.data]); setSharePortalOpen(true); }}
                            sx={{
                                color: tok.text.muted,
                                '&:hover': { color: activeColor, bgcolor: alpha(activeColor, 0.1) },
                                width: 32, height: 32
                            }}
                        >
                            <LuShare2 size={16} />
                        </IconButton>
                    </Tooltip>
                ),
                ...centerAlignColDef,
                suppressMenu: true,
                sortable: false,
                filter: false
            },
            {
                headerName: t("Name"),
                field: "name",
                ...defaultColDef,
                ...centerAlignColDef, // Match user request for centered headers/content
                flex: 2,
                cellStyle: { ...tok.gridCellText, fontWeight: 500 }
            },
            {
                headerName: t("Type"),
                field: "type",
                ...defaultColDef,
                ...centerAlignColDef, // Center aligned
                width: 140,
                valueGetter: (p) => types[p.data?.type] ?? p.data?.type ?? "",
                cellRenderer: p => {
                    const label = types[p.data?.type] ?? p.data?.type ?? "";
                    return (
                        <Typography sx={{
                            ...tok.gridCellText,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: tok.text.primary,
                            textTransform: 'capitalize',
                            letterSpacing: '0.02em'
                        }}>
                            {label}
                        </Typography>
                    );
                }
            },
            { headerName: t("Year"), field: "year", ...defaultColDef, ...centerAlignColDef, width: 100 },
            { headerName: t("GWP (kg CO₂e)"), field: "gwp", ...defaultColDef, ...centerAlignColDef, ...floatFormater, width: 150 },
            { headerName: t("Date Modified"), field: "date_modified", ...defaultColDef, ...centerAlignColDef, width: 160 },
        ].map((colDef) => ({
            ...colDef,
            hide: isHidden(colDef.field),
            headerClass: 'ag-header-cell-centered'
        }));
    }, [columnVisibilities, theme, activeColor, t, tok.gridCellText]);

    const defaultCols = useMemo(() => colDefs.map((col) => col.field), [colDefs]);

    const displayedTemplates = useMemo(() => {
        if (type) return templates.filter((template) => template.type === type);
        return templates;
    }, [templates, type]);

    useEffect(() => {
        PublishedTemplate.get_all()
            .then((res) => res.json())
            .then((data) => setTemplates(data.map((item, index) => ({ ...item, id: index }))));
    }, [refresh]);

    const shareToCompany = useCallback(
        (selectedTemplates) => {
            TemplateSharing.new(selectedTemplates.map((p) => ({ ...p, company_id: selectedCompanyID }))).then(() => {
                gridRef.current?.api?.deselectAll();
                enqueueAlert({ message: "Templates shared successfully", severity: "success" });
                setRefresh((r) => !r);
            }).catch(() => enqueueAlert({ message: "Failed to share templates", severity: "error" }));
        },
        [selectedCompanyID, setRefresh, enqueueAlert]
    );

    const contextMenuOptions = useMemo(
        () => [selectedCompanyID && { label: t("Share to company (Bulk)"), action: () => dialogRef.current?.open(), needSelectedItems: true }].filter(Boolean),
        [selectedCompanyID, t]
    );
    return (
        <Box>
            <Paper variant="Table Container" sx={{ mt: tok.spacing.pageGap }}>
                <TableHeaderContainer>
                    <TableTitleContainer>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(activeColor, 0.1), color: activeColor
                            }}>
                                <LuFileText size={18} />
                            </Box>
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography sx={tok.tableTitle}>
                                    {t("Template Library")} ({displayedTemplates.length})
                                </Typography>
                            </Box>
                        </Stack>
                    </TableTitleContainer>

                    <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                        <Box sx={{ width: 350 }}>
                            <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search library...")} width={350} />
                        </Box>

                        {selectedTemplates.length > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LuShare2 size={16} />}
                                onClick={() => { setShareTarget(selectedTemplates); setSharePortalOpen(true); }}
                                sx={{
                                    height: 40,
                                    bgcolor: alpha(tok.bg.tabActive, 0.1),
                                    borderColor: alpha(tok.bg.tabActive, 0.3),
                                    color: tok.bg.tabActive,
                                    '&:hover': {
                                        bgcolor: tok.bg.tabActive,
                                        color: '#fff',
                                        borderColor: tok.bg.tabActive
                                    },
                                    borderRadius: tok.radius.button,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    px: 2,
                                    fontSize: '0.85rem'
                                }}
                            >
                                {t("Portal Share")} ({selectedTemplates.length})
                            </Button>
                        )}

                        <ExportAsXLSXButton gridRef={gridRef} filename="published_templates" />
                        <ColumnVisibilityButton
                            columns={colDefs}
                            visibleColumns={columnVisibilities[colVisibility_key] || defaultCols}
                            columnVis_key={colVisibility_key}
                        />
                        <RefreshButton onRefresh={() => setRefresh((r) => !r)} />
                    </TableHeaderToolsContainer>
                </TableHeaderContainer>

                <Box>
                    <AgGrid
                        rowData={displayedTemplates}
                        columnDefs={colDefs}
                        onSelectionChanged={(e) => setSelectedTemplates(e.api.getSelectedRows())}
                        handleContextMenu={(e) => {
                            e.preventDefault(); setMenuPosition({ x: e.pageX, y: e.pageY }); setMenuVisible(true);
                        }}
                        quickFilterText={searchText}
                        ref={gridRef}
                        domLayout="autoHeight"
                    />
                </Box>
            </Paper>

            <ColumnMenu
                anchorRef={gridHeaderContainerRef}
                visible={columnMenuVisible}
                onClose={() => setColumnMenuVisible(false)}
                colDefs={colDefs}
                colVisibility_key={colVisibility_key}
                defaultCols={defaultCols}
            />

            <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={() => setMenuVisible(false)} selectedItems={selectedTemplates} options={contextMenuOptions} />

            <DialogGeneral
                selectedItems={selectedTemplates}
                handleAction={shareToCompany}
                ref={dialogRef}
                header={t("Confirm Share")}
                title={t("Are you sure you want to share these templates with the selected organization?")}
                actionName={t("Share")}
            />

            {shareTarget && (
                <SharePortal
                    open={sharePortalOpen}
                    onClose={() => { setSharePortalOpen(false); setShareTarget(null); }}
                    onSuccess={() => { gridRef.current?.api?.deselectAll(); setRefresh(r => !r); }}
                    templates={shareTarget}
                    type={type || (Array.isArray(shareTarget) ? shareTarget[0]?.type?.toLowerCase() : shareTarget?.type?.toLowerCase())}
                />
            )}
        </Box>
    );
};

export default PublishedTemplateGrid;
